import { lockBodyScroll, unlockBodyScroll } from "./utils.js";
import { INTENT, STATE, on, dispatch } from "./ui-events.js";

const SELECTORS = {
  input: 'input[type="search"]',
  results: "[data-predictive-search-results]",
  closeIcon: "[data-predictive-search-icon-close]",
  searchIcon: "[data-predictive-search-icon-search]",
};

class PredictiveSearch extends HTMLElement {
  #cleanupFns = [];
  #scrollLocked = false;

  constructor() {
    super();
    this.preventIOSScroll = this.preventIOSScroll.bind(this);
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this.open = this.open.bind(this);
  }

  connectedCallback() {
    this.#bindElements();
    this.#bindEvents();
  }

  disconnectedCallback() {
    this.#cleanupFns.forEach((fn) => fn());
    this.#cleanupFns = [];
    document.removeEventListener("click", this.handleOutsideClick);
    document.removeEventListener("touchmove", this.preventIOSScroll);
  }

  #bindElements() {
    this.input = this.querySelector(SELECTORS.input);
    this.form = this.querySelector("form");
    this.predictiveSearchResults = this.querySelector(SELECTORS.results);
    this.closeIcon = this.querySelector(SELECTORS.closeIcon);
    this.searchIcon = this.querySelector(SELECTORS.searchIcon);

    // Freebooks liegen nicht im Shopify-Suchindex — client-seitig aus dem
    // eingebetteten JSON-Index matchen (siehe inline-predictive-search.liquid).
    this.freebookIndex = [];
    const indexScript = this.querySelector("[data-freebook-index]");
    if (indexScript) {
      try {
        this.freebookIndex = JSON.parse(indexScript.textContent) || [];
      } catch (error) {
        console.warn("[PredictiveSearch] Invalid freebook index JSON", error);
      }
    }
    this.freebookHeading = this.getAttribute("data-freebook-heading") || "";
  }

  #bindEvents() {
    this.input?.addEventListener(
      "input",
      this.debounce(() => this.onChange(), 300)
    );

    this.form?.addEventListener("reset", () => {
      this.close();
    });

    document.addEventListener("click", this.handleOutsideClick);
    this.input.addEventListener("click", this.open);

    this.closeIcon?.addEventListener("click", () =>
      dispatch(INTENT.SEARCH_CLOSE),
    );

    this.#cleanupFns.push(on(STATE.SEARCH_CLOSED, () => this.close()));
  }

  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  onChange() {
    const searchTerm = this.input.value.trim();

    if (!searchTerm.length) {
      this.close();
      return;
    }

    this.searchIcon?.setAttribute("hidden", "");
    this.closeIcon?.removeAttribute("hidden");

    this.getSearchResults(searchTerm);
  }

  close() {
    this.predictiveSearchResults.setAttribute("hidden", "");

    this.closeIcon?.setAttribute("hidden", "");
    this.searchIcon?.removeAttribute("hidden");

    if (this.#scrollLocked) {
      unlockBodyScroll();
      this.#scrollLocked = false;
      document.removeEventListener("touchmove", this.preventIOSScroll);
    }
  }

  handleOutsideClick(event) {
    const isOpen = !this.predictiveSearchResults.hasAttribute("hidden");

    if (isOpen && !this.contains(event.target)) {
      this.close();
    }
  }

  getSearchResults(searchTerm) {
    fetch(
      `${window.Shopify.routes.root}search/suggest?q=${encodeURIComponent(searchTerm)}&resources[type]=product,page,collection,article&resources[limit]=6&resources[limit_scope]=all&section_id=predictive-search-results`,
    )
      .then((response) => {
        if (!response.ok) {
          const error = new Error(response.status);
          this.close();
          throw error;
        }
        return response.text();
      })
      .then((text) => {
        this.predictiveSearchResults.innerHTML = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector(
            "#shopify-section-predictive-search-results",
          ).innerHTML;
        this.#injectFreebooks(searchTerm);
        this.open();
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  #matchFreebooks(term) {
    const tokens = term.toLowerCase().split(/\s+/).filter(Boolean);
    if (!tokens.length) return [];

    return this.freebookIndex
      .filter((fb) => {
        const haystack = `${fb.title || ""} ${fb.category || ""}`.toLowerCase();
        return tokens.every((token) => haystack.includes(token));
      })
      .slice(0, 6);
  }

  #injectFreebooks(searchTerm) {
    if (!this.freebookIndex.length) return;

    const matches = this.#matchFreebooks(searchTerm);
    if (!matches.length) return;

    const container =
      this.predictiveSearchResults.querySelector("#predictive-search-results") ||
      this.predictiveSearchResults;
    const section = this.#buildFreebookSection(matches);

    const noResults = container.querySelector("[data-predictive-no-results]");
    const showAll = container.querySelector("[data-predictive-show-all]");
    if (noResults) {
      noResults.replaceWith(section);
    } else if (showAll) {
      container.insertBefore(section, showAll);
    } else {
      container.appendChild(section);
    }
  }

  #buildFreebookSection(matches) {
    const section = document.createElement("section");
    section.className = "mb-4 border-t border-(--color-text)/10 pt-4";
    section.setAttribute(
      "aria-labelledby",
      "predictive-search-freebooks-heading",
    );

    const heading = document.createElement("h2");
    heading.id = "predictive-search-freebooks-heading";
    heading.className =
      "uppercase tracking-wider text-xs font-semibold mb-2 px-1 text-(--color-text)/70";
    heading.textContent = this.freebookHeading || "Freebooks";
    section.appendChild(heading);

    const ul = document.createElement("ul");
    ul.className = "space-y-1";

    matches.forEach((fb) => {
      const li = document.createElement("li");
      li.className = "hover:bg-(--color-text)/10 rounded-md p-1";
      li.setAttribute("role", "option");

      const a = document.createElement("a");
      a.className = "flex items-center gap-2";
      a.href = fb.url;

      if (fb.image) {
        const imgWrap = document.createElement("div");
        imgWrap.className = "w-10 h-10 shrink-0";
        const img = document.createElement("img");
        img.src = fb.image;
        img.alt = "";
        img.loading = "lazy";
        img.className = "object-cover w-full h-full rounded";
        imgWrap.appendChild(img);
        a.appendChild(imgWrap);
      }

      const span = document.createElement("span");
      span.className = "text-sm";
      span.textContent = fb.title;
      a.appendChild(span);

      li.appendChild(a);
      ul.appendChild(li);
    });

    section.appendChild(ul);
    return section;
  }

  preventIOSScroll(e) {
    const target = e.target;
    const searchContainer = this.querySelector(SELECTORS.results);

    if (!searchContainer.contains(target)) {
      e.preventDefault();
    }
  }

  open() {
    if (!this.predictiveSearchResults.innerHTML.trim()) return;

    this.predictiveSearchResults.removeAttribute("hidden");

    const isMobile = window.matchMedia("(max-width: 767px)").matches;
    if (isMobile) {
      lockBodyScroll();
      this.#scrollLocked = true;
      document.addEventListener("touchmove", this.preventIOSScroll, {
        passive: false,
      });
    }
  }
}

customElements.define("predictive-search", PredictiveSearch);
